<script setup lang="ts">
import { computed } from 'vue';
import Background from '@/components/Background.vue';
import Copyright from '@/components/Copyright.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ButtonSecondary from '@/shared/components/button/ButtonSecondary.vue';
import { logout } from '@/shared/auth/session';
import { AppContext } from '@/models/AppContext';
import { useAuthStore } from '@/shared/stores/authStore';
import circle from '@/assets/logo/favicon_circlelg.png';
import WarningIcon from '@/shared/assets/icon/warning.svg';

const authStore = useAuthStore();
const logo = computed(() => authStore.displayLogo);

withDefaults(defineProps<{ retryProfileLoad?: boolean; description?: string }>(), {
  retryProfileLoad: true,
  description: 'We could not load your account details. Please try again or sign out.',
});

const isRetrying = computed(() => authStore.lifecycleStatus.profileHydrationStatus === 'loading');

async function onRetry() {
  try {
    await authStore.refreshBackendProfile();
  } catch (error) {
    console.error('Profile hydration retry failed:', error);
  }
}

function onSignOut() {
  void logout(AppContext.SolCentre);
}
</script>

<template>
  <div class="auth-error" role="alert" aria-live="polite">
    <Background />
    <div class="auth-error__container">
      <img class="auth-error__logo__circle" :src="circle" alt="" />
      <img class="auth-error__logo" :src="logo" alt="SolCentre" />
      <div class="auth-error__card">
        <div class="auth-error__icon" aria-hidden="true">
          <WarningIcon />
        </div>
        <h3 class="auth-error__title">Login failed</h3>
        <p class="auth-error__description">{{ description }}</p>
        <div class="auth-error__actions">
          <ButtonPrimary v-if="retryProfileLoad" block :loading="isRetrying" @click="onRetry">
            Try again
          </ButtonPrimary>
          <ButtonSecondary block @click="onSignOut">Sign out</ButtonSecondary>
        </div>
      </div>
    </div>
    <Copyright />
  </div>
</template>

<style lang="less" scoped>
.auth-error {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &__container {
    margin: 0 auto;
    padding-top: 100px;
    width: 390px;
    position: relative;
    z-index: 1;
  }

  &__logo__circle {
    height: 80px;
    display: block;
    margin: 0 auto 30px;
  }

  &__logo {
    display: block;
    margin: 0 auto 30px;
    width: 390px;
  }

  &__icon {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
    color: #08645a;
  }

  &__title {
    text-align: center;
    margin: 0 0 16px;
  }

  &__card {
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 15px 35px 0 rgba(60, 66, 87, 0.1), 0 5px 15px 0 rgba(0, 0, 0, 0.07);
    padding: 60px 50px;
  }

  &__description {
    margin: 0 0 30px;
    text-align: center;
    color: #021e1b;
    font-size: 14px;
    line-height: 1.5;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
